// FILE: admin/src/pages/Errors.jsx
// ─────────────────────────────────────────────────────────────────────────
// Real-time error monitoring for Oráku admins.
//
// What this page does:
//   1. Streams api_logs and security_alerts as they're written by Cloud
//      Functions. New errors appear within ~1s.
//   2. Plays an audible chime when a new error arrives while the tab is
//      open — admins notice issues before users complain.
//   3. Groups errors by category (auth, rate-limit, AI, payments, etc.)
//      and shows trend counts so admins can spot patterns.
//   4. One-click filtering by severity + 24h/7d/30d windows.
//
// Data sources:
//   - security_alerts (rate-limit breaches, blocked prompts, suspicious activity)
//   - api_logs WHERE error == true (function-level failures)
//
// Notification toggle persists in localStorage so admins can mute
// the chime overnight without losing other state.
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  collection, query, orderBy, limit as fbLimit, where,
  onSnapshot, getCountFromServer, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, StatCard, Card, Spinner, Badge, GoldBtn } from '../components/UI';

const ACCENT = '#008751';
const GOLD   = '#D4AF37';
const RED    = '#ef4444';
const ORANGE = '#f97316';
const BLUE   = '#3b82f6';

// Categorize alert types into a severity bucket so admins can scan fast.
const SEVERITY = {
  // Critical — money / data loss / abuse
  payment_intent_uid_mismatch:    'critical',
  payment_intent_tier_mismatch:   'critical',
  image_prompt_blocked:           'critical',
  tts_unknown_voice_id:           'critical',
  // High — likely abuse, but capped
  rate_limit_exceeded:            'high',
  daily_limit_exceeded:           'high',
  image_daily_limit_exceeded:     'high',
  tts_daily_limit_exceeded:       'high',
  // Medium — security policy actions
  content_safety_flag:            'medium',
  suspicious_activity:            'medium',
  // Default fallback
  default:                        'medium',
};

const SEV_COLORS = {
  critical: { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.4)',  text: RED,    icon: '🔴' },
  high:     { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.4)', text: ORANGE, icon: '🟠' },
  medium:   { bg: 'rgba(212,175,55,0.10)', border: 'rgba(212,175,55,0.3)', text: GOLD,   icon: '🟡' },
};

// Friendly labels for the alert types we actually emit from Cloud Functions
const FRIENDLY = {
  rate_limit_exceeded:           'Rate limit hit (per-minute)',
  daily_limit_exceeded:          'Daily request limit hit',
  image_daily_limit_exceeded:    'Image daily cap hit',
  tts_daily_limit_exceeded:      'Voice daily cap hit',
  image_prompt_blocked:          'Blocked image prompt (NSFW/violence)',
  tts_unknown_voice_id:          'Unknown ElevenLabs voiceId attempted',
  payment_intent_uid_mismatch:   'PaymentIntent UID mismatch (someone tried to redeem another user\'s PI)',
  payment_intent_tier_mismatch:  'PaymentIntent tier mismatch (paid for X, asked for Y)',
  content_safety_flag:           'Content safety flag',
  suspicious_activity:           'Suspicious activity',
};

// localStorage keys
const LS_MUTE   = 'oraku_admin_errors_mute';
const LS_LAST_SEEN = 'oraku_admin_errors_last_seen';

export default function Errors() {
  const [alerts, setAlerts]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [muted, setMuted]       = useState(() => localStorage.getItem(LS_MUTE) === '1');
  const [windowHours, setWindowHours] = useState(24); // 24 / 168 / 720
  const [stats, setStats]       = useState({ critical: 0, high: 0, medium: 0, total24h: 0 });
  const [filter, setFilter]     = useState('all');   // all | critical | high | medium

  // Track which alert ids we've already seen so the chime only fires for
  // genuinely new ones — not the initial load.
  const seenIdsRef = useRef(new Set());
  const initialLoadRef = useRef(true);
  const audioCtxRef = useRef(null);

  /* ── live subscription to security_alerts ────────────────────────── */
  useEffect(() => {
    const since = Timestamp.fromDate(new Date(Date.now() - windowHours * 3600 * 1000));
    const q = query(
      collection(db, 'security_alerts'),
      where('createdAt', '>=', since),
      orderBy('createdAt', 'desc'),
      fbLimit(200),
    );

    const unsub = onSnapshot(q, (snap) => {
      const next = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Detect newly-arrived alerts (not present on initial load)
      if (!initialLoadRef.current) {
        const newOnes = next.filter(a => !seenIdsRef.current.has(a.id));
        if (newOnes.length > 0 && !muted) {
          // Beep once for each batch (not per-alert) to avoid noise
          playChime(newOnes.some(a => severityOf(a) === 'critical'));
          if ('vibrate' in navigator) navigator.vibrate?.([80, 40, 80]);
        }
      }
      next.forEach(a => seenIdsRef.current.add(a.id));
      setAlerts(next);
      setLoading(false);
      initialLoadRef.current = false;
      localStorage.setItem(LS_LAST_SEEN, String(Date.now()));
    }, (err) => {
      console.error('Errors snapshot failed:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [windowHours, muted]);

  /* ── 24h summary counts (separate query, runs once) ──────────────── */
  useEffect(() => { loadCounts(); }, [windowHours]);

  async function loadCounts() {
    try {
      const since = Timestamp.fromDate(new Date(Date.now() - windowHours * 3600 * 1000));
      const col = collection(db, 'security_alerts');
      const total = await getCountFromServer(query(col, where('createdAt', '>=', since)));
      setStats(s => ({ ...s, total24h: total.data().count }));
    } catch (e) { console.error(e); }
  }

  /* ── recompute severity buckets whenever alerts change ───────────── */
  useEffect(() => {
    const buckets = { critical: 0, high: 0, medium: 0 };
    alerts.forEach(a => { buckets[severityOf(a)]++; });
    setStats(s => ({ ...s, ...buckets }));
  }, [alerts]);

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    localStorage.setItem(LS_MUTE, next ? '1' : '0');
  }

  /* ── Chime via WebAudio (no asset file needed) ───────────────────── */
  function playChime(isCritical) {
    try {
      if (!audioCtxRef.current) {
        const C = window.AudioContext || window.webkitAudioContext;
        if (!C) return;
        audioCtxRef.current = new C();
      }
      const ctx = audioCtxRef.current;
      // Two-tone alert: high-low. Critical alerts get an extra third tone.
      const tones = isCritical ? [880, 660, 880] : [880, 660];
      tones.forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t0 = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.18);
      });
    } catch (e) {
      console.warn('Chime failed:', e);
    }
  }

  /* ── filtered list ───────────────────────────────────────────────── */
  const filteredAlerts = useMemo(() => {
    if (filter === 'all') return alerts;
    return alerts.filter(a => severityOf(a) === filter);
  }, [alerts, filter]);

  return (
    <div style={{ padding: '36px 36px 80px' }}>
      <PageHeader
        title="🚨 Errors & Alerts"
        subtitle="Real-time monitoring — chime fires when a new error arrives"
        action={
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={() => playChime(true)}
              title="Test the alert sound"
              style={pillBtn('rgba(255,255,255,0.06)', 'rgba(255,255,255,0.6)')}>
              🔔 Test
            </button>
            <button
              onClick={toggleMute}
              style={pillBtn(
                muted ? 'rgba(239,68,68,0.12)' : 'rgba(0,135,81,0.12)',
                muted ? RED : ACCENT,
              )}>
              {muted ? '🔕 Sound Muted' : '🔔 Sound On'}
            </button>
          </div>
        }
      />

      {/* Stat cards */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 14, marginBottom: 24,
      }}>
        <StatCard label="Critical"  value={stats.critical} icon="🔴" color={RED}
                  sub="Security & payment incidents" />
        <StatCard label="High"      value={stats.high}     icon="🟠" color={ORANGE}
                  sub="Rate limits & abuse caps" />
        <StatCard label="Medium"    value={stats.medium}   icon="🟡" color={GOLD}
                  sub="Routine flags" />
        <StatCard label={`Last ${windowHours === 24 ? '24h' : windowHours === 168 ? '7d' : '30d'}`}
                  value={stats.total24h} icon="∑" color={BLUE}
                  sub="Total events in window" />
      </div>

      {/* Window + filter controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          ['Last 24h',  24],
          ['Last 7 days', 168],
          ['Last 30 days', 720],
        ].map(([label, hrs]) => (
          <button key={hrs} onClick={() => setWindowHours(hrs)}
            style={pillBtn(
              windowHours === hrs ? 'rgba(0,135,81,0.18)' : 'rgba(255,255,255,0.04)',
              windowHours === hrs ? ACCENT : 'rgba(255,255,255,0.55)',
            )}>
            {label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {['all', 'critical', 'high', 'medium'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={pillBtn(
              filter === f ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
              filter === f ? '#fff' : 'rgba(255,255,255,0.45)',
            )}>
            {f === 'all' ? 'All' : `${SEV_COLORS[f].icon} ${cap(f)}`}
          </button>
        ))}
      </div>

      {/* Live status banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', marginBottom: 18,
        background: 'rgba(0,135,81,0.06)', border: '1px solid rgba(0,135,81,0.2)',
        borderRadius: 10, fontSize: 13, color: 'rgba(255,255,255,0.7)',
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: ACCENT,
          boxShadow: `0 0 8px ${ACCENT}`,
          animation: 'pulse 1.6s ease-in-out infinite',
        }} />
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
        Live · {filteredAlerts.length} {filter === 'all' ? 'events' : filter + ' events'} in this window
        {muted && (
          <span style={{ marginLeft: 'auto', color: RED, fontSize: 12 }}>
            🔕 Sound is muted
          </span>
        )}
      </div>

      {/* Alerts list */}
      <Card title={`Live feed${filter !== 'all' ? ` — ${cap(filter)} only` : ''}`}>
        {loading ? <Spinner /> : (
          filteredAlerts.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✨</div>
              <div style={{ fontSize: 14 }}>No errors in this window. The platform is healthy.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filteredAlerts.map(a => <ErrorRow key={a.id} alert={a} />)}
            </div>
          )
        )}
      </Card>
    </div>
  );
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function severityOf(a) {
  return SEVERITY[a.alertType] || SEVERITY.default;
}

function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

function pillBtn(bg, color) {
  return {
    padding: '8px 14px',
    background: bg,
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 50,
    color, fontSize: 12, fontWeight: 600,
    fontFamily: 'inherit',
  };
}

/* ── Single error row — compact, expandable on click ─────────────────── */
function ErrorRow({ alert }) {
  const [open, setOpen] = useState(false);
  const sev   = severityOf(alert);
  const color = SEV_COLORS[sev];
  const friendly = FRIENDLY[alert.alertType] || alert.alertType.replace(/_/g, ' ');
  const when = alert.createdAt?.toDate ? alert.createdAt.toDate() : new Date();
  const ago = relTime(when);

  return (
    <div style={{
      background: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      <button onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '12px 14px', background: 'transparent',
          border: 'none', display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer', textAlign: 'left',
        }}>
        <span style={{ fontSize: 18 }}>{color.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13.5, fontWeight: 600, color: '#fff',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{friendly}</div>
          <div style={{
            fontSize: 11.5, color: 'rgba(255,255,255,0.5)', marginTop: 3,
            display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span>{ago}</span>
            <span>·</span>
            <span style={{ fontFamily: 'DM Mono', fontSize: 11 }}>
              {alert.email || alert.uid?.substring(0, 8) || 'unknown'}
            </span>
            {alert.tier && (
              <>
                <span>·</span>
                <Badge label={alert.tier.toUpperCase()} color={alert.tier === 'scholar' ? GOLD : ACCENT} />
              </>
            )}
            {alert.country && (
              <>
                <span>·</span>
                <span>{alert.country}</span>
              </>
            )}
          </div>
        </div>
        <span style={{
          fontSize: 9, fontWeight: 800, padding: '4px 8px',
          borderRadius: 50, background: 'rgba(0,0,0,0.3)',
          color: color.text, letterSpacing: 1,
        }}>
          {sev.toUpperCase()}
        </span>
        <span style={{
          color: 'rgba(255,255,255,0.4)', fontSize: 11,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>▶</span>
      </button>

      {open && (
        <div style={{
          padding: '14px 16px 16px',
          background: 'rgba(0,0,0,0.25)',
          borderTop: `1px solid ${color.border}`,
        }}>
          <DetailRow label="Alert type"  value={alert.alertType} mono />
          <DetailRow label="UID"         value={alert.uid} mono />
          {alert.email && <DetailRow label="Email" value={alert.email} />}
          {alert.displayName && <DetailRow label="Name" value={alert.displayName} />}
          {alert.country && <DetailRow label="Country" value={alert.country} />}
          {alert.tier    && <DetailRow label="Tier"    value={alert.tier} />}
          <DetailRow label="When"  value={when.toLocaleString()} />
          <DetailRow label="Status" value={alert.status || 'open'} />
          {alert.meta && Object.keys(alert.meta).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{
                fontSize: 11, color: 'rgba(255,255,255,0.4)',
                letterSpacing: 1, marginBottom: 6, fontWeight: 600,
              }}>METADATA</div>
              <pre style={{
                fontSize: 11.5, padding: 10, background: 'rgba(0,0,0,0.4)',
                borderRadius: 6, color: 'rgba(255,255,255,0.75)',
                overflow: 'auto', maxHeight: 200,
                fontFamily: 'DM Mono, monospace', margin: 0,
              }}>{JSON.stringify(alert.meta, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: 'flex', gap: 12, padding: '4px 0',
      fontSize: 12.5, alignItems: 'baseline',
    }}>
      <div style={{ width: 90, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>{label}</div>
      <div style={{
        color: '#fff',
        fontFamily: mono ? 'DM Mono, monospace' : 'inherit',
        fontSize: mono ? 11.5 : 12.5,
        wordBreak: 'break-all',
      }}>{value}</div>
    </div>
  );
}

function relTime(date) {
  const ms = Date.now() - date.getTime();
  const s  = Math.floor(ms / 1000);
  if (s < 30)   return 'just now';
  if (s < 60)   return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)    return `${d}d ago`;
  return date.toLocaleDateString();
}
